import { create } from 'zustand'

import type { Job, RealtimeEvent, RealtimeSnapshot } from '../types'
import {
  applyRealtimeEvents,
  emptyEntities,
  hydrateEntities,
  mergeSnapshot,
  restoreJob,
  rollbackJobStaffing,
  type EntitiesState,
  type SnapshotMergeResult,
} from './reducers'

export type SelectionNoticeKind = 'deleted' | 'completed'

interface SelectionState {
  /** The expanded job, shared by the jobs panel, map and live schedule. */
  selectedJobId: string | null
  /** Worker whose info card is open on the map. */
  selectedWorkerId: string | null
  /** Set when another admin deleted/completed the expanded job (card stays open with a banner). */
  selectionNotice: { jobId: string; kind: SelectionNoticeKind } | null
  /** Last known copy of the expanded job after it disappeared from the data. */
  detachedJob: Job | null
}

interface InteractionState {
  /** Worker row / nearby chip <-> map pin hover sync. */
  hoveredWorkerId: string | null
  /** Set when a candidate pin is clicked: the nearby strip scrolls to and highlights that chip. */
  focusedCandidate: { workerId: string; token: number } | null
  /** `${jobId}|${workerId}` -> flash expiry (epoch ms). */
  flashedRows: Record<string, number>
  /** Latest message for the aria-live region. */
  announcement: { id: number; text: string } | null
}

interface RealtimeStoreState extends EntitiesState, SelectionState, InteractionState {
  /** jobId -> highlight expiry (epoch ms). */
  highlightedJobs: Record<string, number>
  /** false while assignments are still loading (worker rows / unassigned counts show loading). */
  staffingLoaded: boolean
  setStaffingLoaded: (loaded: boolean) => void
  hydrate: (snapshot: RealtimeSnapshot) => void
  merge: (snapshot: RealtimeSnapshot) => SnapshotMergeResult
  applyEvents: (events: RealtimeEvent[]) => void
  restoreJob: (job: Job) => void
  rollbackJobStaffing: (previous: Pick<Job, 'id' | 'workers' | 'requiredWorkers' | 'status'>) => void
  highlightJobs: (jobIds: string[], durationMs: number) => void
  pruneHighlights: (now: number) => void
  /** Expands a job; selecting the expanded job again collapses it. */
  toggleJobSelection: (jobId: string) => void
  setSelectedJob: (jobId: string | null) => void
  selectWorker: (workerId: string | null) => void
  clearSelection: () => void
  setSelectionNotice: (notice: SelectionState['selectionNotice']) => void
  setHoveredWorker: (workerId: string | null) => void
  focusCandidate: (workerId: string | null) => void
  flashRows: (keys: string[], durationMs: number) => void
  announce: (text: string) => void
  reset: () => void
}

const pickEntities = (state: RealtimeStoreState): EntitiesState => ({
  workersById: state.workersById,
  workerIds: state.workerIds,
  jobsById: state.jobsById,
  jobIds: state.jobIds,
})

const emptySelection: SelectionState = { selectedJobId: null, selectedWorkerId: null, selectionNotice: null, detachedJob: null }
const emptyInteraction: InteractionState = { hoveredWorkerId: null, focusedCandidate: null, flashedRows: {}, announcement: null }

/**
 * Keeps selection consistent with new entities. A missing worker closes its card; a missing
 * expanded job is kept (as a detached copy) with a "deleted" notice so the admin is told why
 * instead of the card silently disappearing.
 */
export function reconcileSelection(
  selection: SelectionState,
  previous: EntitiesState,
  entities: EntitiesState,
): SelectionState {
  let next = selection
  if (selection.selectedWorkerId && !entities.workersById[selection.selectedWorkerId]) {
    next = { ...next, selectedWorkerId: null }
  }
  const jobId = selection.selectedJobId
  if (jobId && !entities.jobsById[jobId]) {
    const lastKnown = previous.jobsById[jobId] ?? selection.detachedJob
    next = lastKnown
      ? { ...next, detachedJob: lastKnown, selectionNotice: { jobId, kind: 'deleted' } }
      : { ...next, selectedJobId: null, detachedJob: null, selectionNotice: null }
  } else if (jobId && entities.jobsById[jobId] && selection.detachedJob) {
    // The job came back (e.g. a transient refresh gap).
    next = { ...next, detachedJob: null, selectionNotice: next.selectionNotice?.kind === 'deleted' ? null : next.selectionNotice }
  }
  return next
}

const pickSelection = (state: RealtimeStoreState): SelectionState => ({
  selectedJobId: state.selectedJobId,
  selectedWorkerId: state.selectedWorkerId,
  selectionNotice: state.selectionNotice,
  detachedJob: state.detachedJob,
})

let announcementSeq = 0
let focusSeq = 0

export const useRealtimeStore = create<RealtimeStoreState>((set, get) => {
  const commitEntities = (next: EntitiesState) => {
    const state = get()
    const previous = pickEntities(state)
    if (next === previous) return
    set({ ...next, ...reconcileSelection(pickSelection(state), previous, next) })
  }

  return {
    ...emptyEntities,
    ...emptySelection,
    ...emptyInteraction,
    highlightedJobs: {},
    staffingLoaded: true,

    setStaffingLoaded: (loaded) => {
      if (get().staffingLoaded !== loaded) set({ staffingLoaded: loaded })
    },

    hydrate: (snapshot) => {
      commitEntities(hydrateEntities(snapshot))
      set({ highlightedJobs: {} })
    },

    merge: (snapshot) => {
      const result = mergeSnapshot(pickEntities(get()), snapshot)
      commitEntities(result.state)
      return result
    },

    applyEvents: (events) => {
      if (events.length === 0) return
      commitEntities(applyRealtimeEvents(pickEntities(get()), events))
    },

    restoreJob: (job) => commitEntities(restoreJob(pickEntities(get()), job)),

    rollbackJobStaffing: (previous) => commitEntities(rollbackJobStaffing(pickEntities(get()), previous)),

    highlightJobs: (jobIds, durationMs) => {
      if (jobIds.length === 0) return
      const expiresAt = Date.now() + durationMs
      set((state) => ({
        highlightedJobs: jobIds.reduce((acc, id) => ({ ...acc, [id]: expiresAt }), state.highlightedJobs),
      }))
    },

    pruneHighlights: (now) => {
      const { highlightedJobs, flashedRows } = get()
      const keep = (record: Record<string, number>) => Object.fromEntries(Object.entries(record).filter(([, expiresAt]) => expiresAt > now))
      const nextHighlights = keep(highlightedJobs)
      const nextFlashes = keep(flashedRows)
      if (Object.keys(nextHighlights).length !== Object.keys(highlightedJobs).length
        || Object.keys(nextFlashes).length !== Object.keys(flashedRows).length) {
        set({ highlightedJobs: nextHighlights, flashedRows: nextFlashes })
      }
    },

    toggleJobSelection: (jobId) => set((state) => ({
      ...emptySelection,
      selectedJobId: state.selectedJobId === jobId || !state.jobsById[jobId] ? null : jobId,
      focusedCandidate: null,
    })),

    setSelectedJob: (jobId) => set((state) => ({
      ...emptySelection,
      selectedJobId: jobId && state.jobsById[jobId] ? jobId : null,
      focusedCandidate: state.selectedJobId === jobId ? state.focusedCandidate : null,
    })),

    selectWorker: (workerId) => set({ selectedWorkerId: workerId }),

    clearSelection: () => set({ ...emptySelection, focusedCandidate: null, hoveredWorkerId: null }),

    setSelectionNotice: (notice) => set({ selectionNotice: notice }),

    setHoveredWorker: (workerId) => {
      if (get().hoveredWorkerId !== workerId) set({ hoveredWorkerId: workerId })
    },

    focusCandidate: (workerId) => {
      focusSeq += 1
      set({ focusedCandidate: workerId ? { workerId, token: focusSeq } : null, hoveredWorkerId: workerId })
    },

    flashRows: (keys, durationMs) => {
      if (keys.length === 0) return
      const expiresAt = Date.now() + durationMs
      set((state) => ({ flashedRows: keys.reduce((acc, key) => ({ ...acc, [key]: expiresAt }), state.flashedRows) }))
    },

    announce: (text) => {
      announcementSeq += 1
      set({ announcement: { id: announcementSeq, text } })
    },

    reset: () => set({ ...emptyEntities, ...emptySelection, ...emptyInteraction, highlightedJobs: {}, staffingLoaded: true }),
  }
})
